/**
 * PaymentModal Component
 * Handles Stripe Checkout redirect for paid rituals
 */

import { useEffect, useRef } from "react"
import { buyRitual } from "../../services/ritual-membership.api"
import type { RitualDetailData } from "../../types/detail.types"

// ============================================================================
// Props
// ============================================================================

interface PaymentModalProps {
  ritual: RitualDetailData
  onSuccess?: () => void
  onError?: (error: string) => void
  onClose?: () => void
}

// ============================================================================
// Main Component - Redirects to Stripe Checkout
// ============================================================================

export function PaymentModal(props: PaymentModalProps) {
  const { ritual, onClose } = props
  const hasRedirected = useRef(false)

  useEffect(() => {
    // Prevent double redirects (React Strict Mode or re-renders)
    if (hasRedirected.current) {
      return
    }
    hasRedirected.current = true

    const redirectToCheckout = async () => {
      try {
        // Build success and cancel URLs
        const baseUrl = window.location.origin
        const successUrl = `${baseUrl}/rituals/${ritual.id}?payment=success`
        const cancelUrl = `${baseUrl}/rituals/${ritual.id}?payment=cancelled`

        // Create checkout session and redirect
        const response = await buyRitual(ritual.id, successUrl, cancelUrl)

        if (response.success && response.data.checkoutUrl) {
          // Call onClose before redirecting (to reset button state)
          onClose?.()
          // Small delay to ensure state update before redirect
          setTimeout(() => {
            // Redirect to Stripe Checkout
            window.location.href = response.data.checkoutUrl
          }, 100)
        } else {
          props.onError?.("Failed to create checkout session")
          onClose?.()
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Failed to initialize payment"
        props.onError?.(message)
        onClose?.()
      }
    }

    redirectToCheckout()
  }, [ritual.id, onClose, props.onError])

  // Show loading state while redirecting
  return null
}

export default PaymentModal
